import { useRef, useCallback } from 'react';

/**
 * Whenever an async callback gets called with `cancelPrevious(callback)`, any previous callbacks
 * that had been wrapped in the same way will throw `Error('cancelled')`
 *
 * This is useful so stale actions aren't able to modify state.
 *
 * For example, an Input being changed can trigger a long running async callback. Once it completes,
 * it might set loading to false, set the errors on a field, or set the value for another variable.
 * However, if the Input is changed again, the previous modification is stale, and allowing it to
 * modify state could enable a submit button when the current value is actually invalid, or show
 * an error while the value is actually valid.
 *
 * The callbacks used don't necessarily need to be the same function, as long as one execution
 * supercedes the previous.
 **/
export const ERROR_CANCELED = 'cancelled';

const useCancelPrevious = () => {
  // Records the state of the last callback run
  const prevState = useRef();

  const cancelPrevious = useCallback(
    (promise) =>
      new Promise((resolve, reject) => {
        // Store the state in an object so it's stored in the ref as a reference to the original object,
        // and changes can still be accessed from inside this function
        let state = { cancelled: false, completed: false };
        if (prevState.current) {
          prevState.current.cancelled = true;
        }
        // Even if the previous reference is removed from the ref, it's still stored in the execution
        // context of the previous callback run. If that function run ends, it'll be garbage collected,
        // so this won't accumulate data like as if this ref was an array with the state of previous runs
        prevState.current = state;
        return promise
          .then((result) => {
            state.completed = true;
            if (!state.cancelled) {
              resolve(result);
            } else {
              reject(ERROR_CANCELED);
            }
          })
          .catch((err) => {
            state.completed = true;
            if (!state.cancelled) {
              reject(err);
            } else {
              reject(ERROR_CANCELED);
            }
          });
      }),
    []
  );

  // Check if there's an async validation running, and doesn't let a sync validation set the loading
  // to false in that case until it completes
  const hasOngoingPromise = useCallback(() => {
    if (prevState.current) return !prevState.current.completed;
    return false;
  }, [prevState]);

  return [cancelPrevious, hasOngoingPromise];
};

export default useCancelPrevious;
